import apiClient from './apiClient';

class SettingsService {
  // Get all settings
  async getSettings() {
    try {
      const response = await apiClient.get('/settings');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get settings error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch settings'
      };
    }
  }

  // Update organization info
  async updateOrganizationInfo(organizationName) {
    try {
      const response = await apiClient.put('/settings/organization', { organizationName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update organization info error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update organization info'
      };
    }
  }

  // Region operations
  async addRegion(regionName) {
    try {
      const response = await apiClient.post('/settings/regions', { regionName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add region error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add region'
      };
    }
  }

  async updateRegion(regionName, newName) {
    try {
      const response = await apiClient.put(`/settings/regions/${encodeURIComponent(regionName)}`, { newName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update region error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update region'
      };
    }
  }

  async removeRegion(regionName) {
    try {
      const response = await apiClient.delete(`/settings/regions/${encodeURIComponent(regionName)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove region error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove region'
      };
    }
  }

  // Managing Group operations
  async addManagingGroup(groupName) {
    try {
      const response = await apiClient.post('/settings/managing-groups', { groupName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add managing group error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add managing group'
      };
    }
  }

  async updateManagingGroup(groupName, newName) {
    try {
      const response = await apiClient.put(`/settings/managing-groups/${encodeURIComponent(groupName)}`, { newName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update managing group error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update managing group'
      };
    }
  }

  async removeManagingGroup(groupName) {
    try {
      const response = await apiClient.delete(`/settings/managing-groups/${encodeURIComponent(groupName)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove managing group error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove managing group'
      };
    }
  }

  // Group Owner operations
  async addGroupOwner(ownerName) {
    try {
      const response = await apiClient.post('/settings/group-owners', { ownerName });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to add group owner' };
    }
  }

  async updateGroupOwner(ownerName, newName) {
    try {
      const response = await apiClient.put(`/settings/group-owners/${encodeURIComponent(ownerName)}`, { newName });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update group owner' };
    }
  }

  async removeGroupOwner(ownerName) {
    try {
      const response = await apiClient.delete(`/settings/group-owners/${encodeURIComponent(ownerName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to remove group owner' };
    }
  }

  async assignManagingGroupToOwner(ownerName, managingGroupNames) {
    try {
      const response = await apiClient.post(`/settings/group-owners/${encodeURIComponent(ownerName)}/managing-groups`, { managingGroupNames });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to assign managing groups' };
    }
  }

  async removeManagingGroupFromOwner(ownerName, managingGroupName) {
    try {
      const response = await apiClient.delete(`/settings/group-owners/${encodeURIComponent(ownerName)}/managing-groups/${encodeURIComponent(managingGroupName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to remove managing group from owner' };
    }
  }

  // Department Group Preset operations
  async addDepartmentGroupPreset(name, groups) {
    try {
      const response = await apiClient.post('/settings/dept-group-presets', { name, groups });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to create preset' };
    }
  }

  async updateDepartmentGroupPreset(presetName, newName, groups) {
    try {
      const response = await apiClient.put(`/settings/dept-group-presets/${encodeURIComponent(presetName)}`, { newName, groups });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update preset' };
    }
  }

  async removeDepartmentGroupPreset(presetName) {
    try {
      const response = await apiClient.delete(`/settings/dept-group-presets/${encodeURIComponent(presetName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to remove preset' };
    }
  }

  async getDepartmentGroupPresets() {
    try {
      const response = await apiClient.get('/settings/dept-group-presets');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch presets' };
    }
  }

  // PDF Profile operations
  async getPdfProfiles() {
    try {
      const response = await apiClient.get('/settings/pdf-profiles');
      return { success: true, data: response.data?.data || [] };
    } catch (error) {
      return { success: false, data: [], message: error.response?.data?.message || 'Failed to fetch PDF profiles' };
    }
  }

  async savePdfProfile(profile) {
    try {
      const response = await apiClient.post('/settings/pdf-profiles', profile);
      return { success: true, data: response.data?.data || [] };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to save PDF profile' };
    }
  }

  async deletePdfProfile(profileName) {
    try {
      const response = await apiClient.delete(`/settings/pdf-profiles/${encodeURIComponent(profileName)}`);
      return { success: true, data: response.data?.data || [] };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete PDF profile' };
    }
  }

  // Targeting Filter operations
  async getTargetingFilters() {
    try {
      const response = await apiClient.get('/settings/targeting-filters');
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch targeting filters' };
    }
  }

  async addTargetingFilter(name, keywords, matchMode, businessUnits) {
    try {
      const response = await apiClient.post('/settings/targeting-filters', { name, keywords, matchMode, businessUnits });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to save targeting filter' };
    }
  }

  async updateTargetingFilter(filterName, newName, keywords, matchMode, businessUnits) {
    try {
      const response = await apiClient.put(`/settings/targeting-filters/${encodeURIComponent(filterName)}`, { newName, keywords, matchMode, businessUnits });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update targeting filter' };
    }
  }

  async removeTargetingFilter(filterName) {
    try {
      const response = await apiClient.delete(`/settings/targeting-filters/${encodeURIComponent(filterName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to remove targeting filter' };
    }
  }

  // Saved Target Selection operations
  async getTargetSelections() {
    try {
      const response = await apiClient.get('/settings/target-selections');
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch saved selections' };
    }
  }

  async addTargetSelection(name, technicalPaths, displayPaths, people) {
    try {
      const response = await apiClient.post('/settings/target-selections', { name, technicalPaths, displayPaths, people });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to save selection' };
    }
  }

  async updateTargetSelection(selectionName, newName, technicalPaths, displayPaths, people) {
    try {
      const response = await apiClient.put(`/settings/target-selections/${encodeURIComponent(selectionName)}`, { newName, technicalPaths, displayPaths, people });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update selection' };
    }
  }

  async removeTargetSelection(selectionName) {
    try {
      const response = await apiClient.delete(`/settings/target-selections/${encodeURIComponent(selectionName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to remove selection' };
    }
  }

  // Industry operations
  async addIndustry(industryName) {
    try {
      const response = await apiClient.post('/settings/industries', { industryName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add industry error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add industry'
      };
    }
  }

  async updateIndustry(industryName, newName) {
    try {
      const response = await apiClient.put(`/settings/industries/${encodeURIComponent(industryName)}`, { newName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update industry error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update industry'
      };
    }
  }

  async removeIndustry(industryName) {
    try {
      const response = await apiClient.delete(`/settings/industries/${encodeURIComponent(industryName)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove industry error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove industry'
      };
    }
  }

  // Country operations
  async addCountry(countryName) {
    try {
      const response = await apiClient.post('/settings/countries', { countryName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add country error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add country'
      };
    }
  }

  async updateCountry(countryName, newName) {
    try {
      const response = await apiClient.put(`/settings/countries/${encodeURIComponent(countryName)}`, { newName });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update country error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update country'
      };
    }
  }

  async removeCountry(countryName) {
    try {
      const response = await apiClient.delete(`/settings/countries/${encodeURIComponent(countryName)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove country error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove country'
      };
    }
  }

  // Celebration GIF operations
  
  // Upload GIF to server filesystem first, then save reference to settings
  async uploadCelebrationGif(base64Data, filename) {
    try {
      const response = await apiClient.post('/upload/celebration-gif', {
        data: base64Data,
        filename
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Upload celebration GIF error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload image'
      };
    }
  }

  async addCelebrationGif(gif) {
    try {
      const response = await apiClient.post('/settings/celebration-gifs', gif);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Add celebration GIF error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add celebration GIF'
      };
    }
  }

  // Delete GIF from server filesystem
  async deleteUploadedGif(filename) {
    try {
      const response = await apiClient.delete(`/upload/celebration-gif/${encodeURIComponent(filename)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Delete uploaded GIF error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete uploaded image'
      };
    }
  }

  async removeCelebrationGif(gifId) {
    try {
      const response = await apiClient.delete(`/settings/celebration-gifs/${encodeURIComponent(gifId)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Remove celebration GIF error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove celebration GIF'
      };
    }
  }

  // Public endpoint - no auth required (for quiz results page)
  async getPublicCelebrationGifs() {
    try {
      const response = await apiClient.get('/settings/public/celebration-gifs');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Get public celebration GIFs error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch celebration GIFs'
      };
    }
  }
}

const settingsService = new SettingsService();
export default settingsService;